import { habitRepository } from '../db/database';
import {
  AUTO_SOURCES,
  DEFAULT_COEFFICIENT,
  DEFAULT_SUCCESS_SCORE,
  type AutoSource,
  type Habit,
  type HabitKind,
  type IconName,
} from '../models/types';
import { todayIso } from '../utils/date';
import { setDelegatedClick } from '../ui/delegatedClick';
import { renderIcon } from '../ui/icons';
import {
  attachIconPicker,
  renderIconPicker,
  HABIT_ICON_CHOICES,
} from '../ui/iconPicker';
import type { RouteId } from '../ui/tabBar';

/**
 * Alışkanlık yönetimi — ekle / düzenle / arşivle.
 *
 * Ekran iki hâlden birinde bulunur: liste ya da form. Bunun için ayrı
 * bir rota açmıyoruz çünkü yönlendirici (router) parametre taşımıyor;
 * "hangi alışkanlığı düzenliyoruz" bilgisi bu modülün kendi
 * durumunda tutuluyor.
 */

const KIND_LABELS: Record<HabitKind, string> = {
  boolean: 'Yapıldı / Yapılmadı',
  partial: 'Parçalı (%50 / %75 / %100)',
  auto: 'Otomatik (sistem hesaplar)',
};

/** Otomatik kaynakların ekranda görünen adları. */
const AUTO_SOURCE_LABELS: Record<AutoSource, string> = {
  cleaning: 'Temizlik görevleri',
};

/** Ekranın o anki hâli. */
type ScreenMode =
  | { type: 'list' }
  /** `habit` yoksa yeni alışkanlık ekleniyor demektir. */
  | { type: 'form'; habit: Habit | undefined };

let mode: ScreenMode = { type: 'list' };
/** Arşivleme onayı bekleyen alışkanlığın id'si. */
let pendingArchiveId: string | null = null;

function escapeHtml(text: string): string {
  const element = document.createElement('span');
  element.textContent = text;
  return element.innerHTML;
}

/**
 * Ondalıklı sayı okur — hem "1.5" hem "1,5" kabul eder.
 *
 * Türkçe klavyede ondalık ayıracı virgüldür ve `Number("1,5")`
 * doğrudan NaN verir. Kullanıcının virgülle yazması yüzünden form
 * reddedilmemeli.
 */
function parseDecimal(value: string): number {
  return Number(value.trim().replace(',', '.'));
}

/* ==================================================================
   Liste
   ================================================================== */

/**
 * Alışkanlığın puan/katsayı özeti.
 * Katsayı 1 ise yazmıyoruz — çarpan yokken göstermek gürültü olurdu.
 */
function describeScoring(habit: Habit): string {
  const score = `${habit.successScore} puan`;
  return habit.coefficient === 1 ? score : `${score} × ${habit.coefficient}`;
}

function renderHabitRow(habit: Habit): string {
  return `
    <li class="manage-row">
      <button type="button" class="manage-row__main" data-edit="${habit.id}">
        <span class="habit-row__icon">${renderIcon(habit.icon, { size: 19 })}</span>
        <span class="manage-row__text">
          <span class="manage-row__name">${escapeHtml(habit.title)}</span>
          <span class="manage-row__meta">${KIND_LABELS[habit.kind]} · ${describeScoring(habit)}</span>
        </span>
      </button>
      <button type="button" class="manage-row__delete" data-archive="${habit.id}"
              aria-label="${escapeHtml(habit.title)} alışkanlığını sil">Sil</button>
    </li>
  `;
}

/**
 * Arşivleme onayı.
 *
 * Silme, geçmiş puan kayıtlarını etkilemez ama alışkanlık bir daha
 * listede görünmez — bu yüzden tek dokunuşla yapılmıyor.
 */
function renderArchiveConfirm(habit: Habit): string {
  return `
    <div class="challenge-confirm">
      <p class="challenge-confirm__title">
        “${escapeHtml(habit.title)}” silinsin mi?
      </p>
      <p class="challenge-confirm__text">
        Alışkanlık listeden kalkar ve bundan sonraki günlerin hesabına girmez.
        Geçmiş puan kayıtların olduğu gibi korunur.
      </p>
      <div class="challenge-confirm__actions">
        <button type="button" class="challenge-confirm__no" data-abort-archive>Vazgeç</button>
        <button type="button" class="challenge-confirm__yes" data-confirm-archive="${habit.id}">
          Evet, sil
        </button>
      </div>
    </div>
  `;
}

/**
 * Arşivlenmiş alışkanlıklar bölümü.
 *
 * Silme geri alınamaz görünmesin diye var: kayıt zaten korunuyordu
 * (§7.2 gereği hard-delete yok), ama arayüzden ulaşılamıyordu.
 * Yanlışlıkla silinen bir alışkanlık buradan geri getirilebilir.
 */
function renderArchivedSection(archived: Habit[]): string {
  if (archived.length === 0) return '';

  const rows = archived
    .map(
      (habit) => `
        <li class="manage-row manage-row--archived">
          <span class="manage-row__main">
            <span class="habit-row__icon">${renderIcon(habit.icon, { size: 19 })}</span>
            <span class="manage-row__text">
              <span class="manage-row__name">${escapeHtml(habit.title)}</span>
              <span class="manage-row__meta">${KIND_LABELS[habit.kind]}</span>
            </span>
          </span>
          <button type="button" class="manage-row__restore" data-restore="${habit.id}">
            Geri Getir
          </button>
        </li>
      `,
    )
    .join('');

  return `
    <h2 class="section-title section-title--spaced">Arşivlenenler</h2>
    <ul class="manage-list" role="list">${rows}</ul>
  `;
}

function renderList(habits: Habit[], archived: Habit[]): string {
  const pending = habits.find((habit) => habit.id === pendingArchiveId);

  const list = habits.length
    ? `<ul class="manage-list" role="list">${habits.map(renderHabitRow).join('')}</ul>`
    : `<div class="placeholder">
         <p class="placeholder__title">Henüz alışkanlık yok</p>
         <p class="placeholder__hint">Aşağıdaki butondan ilk alışkanlığını ekle.</p>
       </div>`;

  return `
    <div class="cleaning-page">
      <header class="detail-header">
        <button type="button" class="back-button" data-back aria-label="Profile dön">
          <svg width="8" height="14" viewBox="0 0 8 14" aria-hidden="true" focusable="false">
            <path d="M7 1L1 7l6 6" stroke="currentColor" stroke-width="2" fill="none"
                  stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <h1 class="detail-header__title">Alışkanlıklar</h1>
      </header>
      ${list}
      ${pending ? renderArchiveConfirm(pending) : ''}
      ${renderArchivedSection(archived)}
      <div class="fab-bar">
        <button type="button" class="fab" data-new>
          ${renderIcon('pencil', { size: 17, color: 'white' })}
          Yeni Alışkanlık
        </button>
      </div>
    </div>
  `;
}

/* ==================================================================
   Form
   ================================================================== */

/**
 * Tür seçeneklerini üretir.
 *
 * "Otomatik" yalnızca, arkasında fiilen çalışan boşta bir kural varsa
 * seçilebilir. Hesaplayanı olmayan otomatik bir alışkanlık her gün
 * "yapılmadı" kalır ve §7.2'ye göre her gün ceza yazardı — kullanıcı
 * hiçbir şey yapmadığı hâlde puan kaybederdi.
 */
function renderKindOptions(selected: HabitKind, availableSources: AutoSource[]): string {
  return (Object.keys(KIND_LABELS) as HabitKind[])
    .map((kind) => {
      const disabled = kind === 'auto' && availableSources.length === 0;
      return `
        <option value="${kind}" ${kind === selected ? 'selected' : ''} ${disabled ? 'disabled' : ''}>
          ${KIND_LABELS[kind]}
        </option>
      `;
    })
    .join('');
}

/**
 * Otomatik kaynak seçici — yalnızca tür "Otomatik" iken görünür.
 *
 * Her kaynak en fazla bir alışkanlığa bağlanabilir: iki alışkanlık
 * aynı kuraldan beslenirse ikisi de aynı sonucu alır, bu da puanı
 * sebepsiz ikiye katlardı.
 */
function renderAutoSourceField(
  selected: AutoSource | undefined,
  availableSources: AutoSource[],
  visible: boolean,
): string {
  const options = availableSources
    .map(
      (source) => `
        <option value="${source}" ${source === selected ? 'selected' : ''}>
          ${AUTO_SOURCE_LABELS[source]}
        </option>
      `,
    )
    .join('');

  return `
    <label class="field" data-auto-source-field ${visible ? '' : 'hidden'}>
      <span class="field__label">Neye göre hesaplansın</span>
      <select class="field__input" name="autoSource">${options}</select>
    </label>
  `;
}

function renderForm(habit: Habit | undefined, availableSources: AutoSource[]): string {
  const isNew = habit === undefined;
  const icon = habit?.icon ?? 'cube';
  const kind = habit?.kind ?? 'boolean';

  return `
    <header class="detail-header">
      <button type="button" class="back-button" data-back-to-list aria-label="Listeye dön">
        <svg width="8" height="14" viewBox="0 0 8 14" aria-hidden="true" focusable="false">
          <path d="M7 1L1 7l6 6" stroke="currentColor" stroke-width="2" fill="none"
                stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <h1 class="detail-header__title">${isNew ? 'Yeni Alışkanlık' : 'Düzenle'}</h1>
    </header>

    <!--
      novalidate: tarayıcının kendi doğrulama balonlarını kapatır.
      Onlar iOS Safari'de çoğu zaman görünmüyor ve görünmedikleri hâlde
      formun gönderilmesini engelliyorlar — kullanıcı butona basıp
      hiçbir şey olmadığını görüyordu. Doğrulamayı kendimiz yapıp
      hatayı sayfada gösteriyoruz.
    -->
    <form class="challenge-form" data-habit-form novalidate>
      <label class="field">
        <span class="field__label">İsim</span>
        <input class="field__input" type="text" name="title" maxlength="40"
               value="${escapeHtml(habit?.title ?? '')}" placeholder="Örn. Kitap oku" />
      </label>

      <div class="field">
        <span class="field__label">İkon</span>
        ${renderIconPicker(icon, HABIT_ICON_CHOICES)}
      </div>

      <label class="field">
        <span class="field__label">Tür</span>
        <select class="field__input" name="kind">${renderKindOptions(kind, availableSources)}</select>
      </label>
      ${renderAutoSourceField(habit?.autoSource, availableSources, kind === 'auto')}

      <div class="field-row">
        <label class="field">
          <span class="field__label">Başarı Puanı</span>
          <input class="field__input" type="text" inputmode="decimal" name="successScore"
                 value="${habit?.successScore ?? DEFAULT_SUCCESS_SCORE}" />
        </label>
        <label class="field">
          <span class="field__label">Katsayı</span>
          <input class="field__input" type="text" inputmode="decimal" name="coefficient"
                 value="${habit?.coefficient ?? DEFAULT_COEFFICIENT}" />
        </label>
      </div>

      <p class="challenge-form__error" data-form-error hidden></p>
      <button type="submit" class="challenge-form__submit">
        ${isNew ? 'Ekle' : 'Kaydet'}
      </button>
    </form>
  `;
}

/* ==================================================================
   Kaydetme
   ================================================================== */

interface FormValues {
  title: string;
  icon: IconName;
  kind: HabitKind;
  autoSource?: AutoSource;
  successScore: number;
  coefficient: number;
}

/** Formu okur; geçersizse hata metni döndürür. */
function readForm(form: HTMLFormElement): FormValues | string {
  const title = (form.elements.namedItem('title') as HTMLInputElement).value.trim();
  const icon = (form.elements.namedItem('icon') as HTMLInputElement).value as IconName;
  const kind = (form.elements.namedItem('kind') as HTMLSelectElement).value as HabitKind;
  const autoSourceField = form.elements.namedItem('autoSource') as HTMLSelectElement | null;
  const successScore = parseDecimal(
    (form.elements.namedItem('successScore') as HTMLInputElement).value,
  );
  const coefficient = parseDecimal(
    (form.elements.namedItem('coefficient') as HTMLInputElement).value,
  );

  if (!title) return 'Alışkanlığa bir isim ver.';
  // Sıfır ya da negatif değer puanlamayı anlamsızlaştırır: katkı da
  // ceza da ters işaretli çıkardı.
  if (!Number.isFinite(successScore) || successScore <= 0) {
    return 'Başarı Puanı 0’dan büyük bir sayı olmalı.';
  }
  if (!Number.isFinite(coefficient) || coefficient <= 0) {
    return 'Katsayı 0’dan büyük bir sayı olmalı.';
  }

  if (kind !== 'auto') {
    // Tür otomatikten başka bir şeye çevrildiyse eski kaynak bağı
    // temizlenmeli, yoksa kaynak boşta görünmez ve başka bir
    // alışkanlığa bağlanamazdı.
    return { title, icon, kind, successScore, coefficient };
  }

  const autoSource = autoSourceField?.value as AutoSource | undefined;
  if (!autoSource) {
    return 'Otomatik alışkanlık için bir hesaplama kaynağı seç.';
  }

  return { title, icon, kind, autoSource, successScore, coefficient };
}

async function saveHabit(existing: Habit | undefined, values: FormValues): Promise<void> {
  if (existing) {
    // Alanları tek tek yazıyoruz (`...existing` yayılımı yerine):
    // tür otomatikten başka bir şeye çevrildiğinde eski `autoSource`
    // bağı da düşsün. Yayılım kullansaydık o alan kayıtta kalır ve
    // kaynak başka bir alışkanlığa bağlanamaz hâle gelirdi.
    await habitRepository.upsert({
      id: existing.id,
      category: existing.category,
      sortOrder: existing.sortOrder,
      createdAt: existing.createdAt,
      archived: existing.archived,
      ...values,
    });
    return;
  }

  const all = await habitRepository.list();
  await habitRepository.upsert({
    id: crypto.randomUUID(),
    ...values,
    // Kategori artık formda sorulmuyor; yeni alışkanlıklar
    // sınıflandırılmamış sayılır.
    category: 'custom',
    // Listenin en sonuna eklensin.
    sortOrder: all.length,
    createdAt: todayIso(),
    archived: false,
  });
}

/* ==================================================================
   Çizim ve olaylar
   ================================================================== */

async function draw(
  container: HTMLElement,
  navigate: (route: RouteId) => Promise<void>,
): Promise<void> {
  const [habits, archived] = await Promise.all([
    habitRepository.listActive(),
    habitRepository.listArchived(),
  ]);
  const redraw = (): Promise<void> => draw(container, navigate);

  // Modül düzeyindeki `mode` her an değişebildiği için TypeScript onu
  // dallanma sonrasında daraltamıyor; yerel bir kopyaya alıyoruz.
  const current = mode;

  if (current.type === 'list') {
    container.innerHTML = renderList(habits, archived);
    attachListHandlers(container, navigate, redraw);
    return;
  }

  // Boşta olan otomatik kaynaklar: başka bir alışkanlığın kullanmadığı
  // kaynaklar. Düzenlenen alışkanlığın KENDİ kaynağı boşta sayılır,
  // yoksa kendi kaydını açtığında seçim listesinden düşerdi.
  const takenByOthers = new Set(
    habits
      .filter((habit) => habit.id !== current.habit?.id)
      .map((habit) => habit.autoSource)
      .filter((source): source is AutoSource => source !== undefined),
  );
  const availableSources = AUTO_SOURCES.filter((source) => !takenByOthers.has(source));

  container.innerHTML = renderForm(current.habit, availableSources);
  attachFormHandlers(container, redraw);
}

function attachListHandlers(
  container: HTMLElement,
  navigate: (route: RouteId) => Promise<void>,
  redraw: () => Promise<void>,
): void {
  // Liste her işlemden sonra kendini yeniden çiziyor. `setDelegatedClick`
  // bir önceki çizimin dinleyicisini kaldırır — aksi halde dinleyiciler
  // üst üste birikir ve tek dokunuş birden çok kez işlenirdi
  // (örn. bir alışkanlık iki kez arşivlenmeye çalışılırdı).
  setDelegatedClick('habitEdit', container, (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-back]')) {
      void navigate('profile');
      return;
    }

    if (target.closest('[data-new]')) {
      mode = { type: 'form', habit: undefined };
      void redraw();
      return;
    }

    const editButton = target.closest<HTMLElement>('[data-edit]');
    if (editButton) {
      void (async () => {
        const habit = await habitRepository.get(editButton.dataset['edit'] ?? '');
        mode = { type: 'form', habit };
        await redraw();
      })();
      return;
    }

    const archiveButton = target.closest<HTMLElement>('[data-archive]');
    if (archiveButton) {
      pendingArchiveId = archiveButton.dataset['archive'] ?? null;
      void redraw();
      return;
    }

    if (target.closest('[data-abort-archive]')) {
      pendingArchiveId = null;
      void redraw();
      return;
    }

    const restoreButton = target.closest<HTMLElement>('[data-restore]');
    if (restoreButton) {
      void (async () => {
        await habitRepository.restore(restoreButton.dataset['restore'] ?? '');
        await redraw();
      })();
      return;
    }

    const confirmButton = target.closest<HTMLElement>('[data-confirm-archive]');
    if (confirmButton) {
      void (async () => {
        // Hard-delete YOK: geçmiş puan kayıtları bu alışkanlığın
        // katkısıyla hesaplandı ve §7.2 gereği yeniden hesaplanamaz.
        await habitRepository.archive(confirmButton.dataset['confirmArchive'] ?? '');
        pendingArchiveId = null;
        await redraw();
      })();
    }
  });
}

function attachFormHandlers(container: HTMLElement, redraw: () => Promise<void>): void {
  const form = container.querySelector<HTMLFormElement>('[data-habit-form]');
  if (!form) return;

  container.querySelector('[data-back-to-list]')?.addEventListener('click', () => {
    mode = { type: 'list' };
    void redraw();
  });

  attachIconPicker(container);

  // Kaynak seçici yalnızca tür "Otomatik" iken anlamlı; tür
  // değiştikçe görünürlüğünü güncelliyoruz.
  const kindField = form.elements.namedItem('kind') as HTMLSelectElement;
  const autoSourceField = container.querySelector<HTMLElement>('[data-auto-source-field]');
  kindField.addEventListener('change', () => {
    if (autoSourceField) autoSourceField.hidden = kindField.value !== 'auto';
  });

  const error = container.querySelector<HTMLElement>('[data-form-error]');

  form.addEventListener('submit', (event) => {
    event.preventDefault();

    const result = readForm(form);
    if (typeof result === 'string') {
      if (error) {
        error.textContent = result;
        error.hidden = false;
      }
      return;
    }

    void (async () => {
      const editing = mode.type === 'form' ? mode.habit : undefined;
      await saveHabit(editing, result);
      mode = { type: 'list' };
      await redraw();
    })();
  });
}

/* ==================================================================
   Giriş noktası
   ================================================================== */

export async function renderHabitsPage(
  container: HTMLElement,
  navigate: (route: RouteId) => Promise<void>,
): Promise<void> {
  // Ekrana her GİRİŞTE listeden başla — kullanıcı başka bir sekmeye
  // gidip döndüğünde yarım kalmış bir formla karşılaşmasın.
  mode = { type: 'list' };
  pendingArchiveId = null;
  await draw(container, navigate);
}
